/**
 * Sidebar derecho de detalle de semana (patrón AlertsDrawer de HomePage2).
 *
 * Entradas de compra de la semana con dos agrupaciones conmutables:
 * - FORMATO: cada material se asigna a los formatos cuyos artículos lo
 *   consumen (vía BOM del motor) — vista de cobertura por línea de negocio.
 * - ENTRADA: por pedido de compra (proveedor + fecha) — vista para reclamar.
 *
 * Grupos colapsados con cabecera-resumen, buscador en vivo, chip "solo en
 * rotura", orden por cantidad/fecha y top-5 líneas con "ver más".
 */
import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { planProduccionApi } from '@/services/api'
import type { PlanEntradaDetalle, PlanFeed, PlanSemana } from '@/types'
import { PlanEngine, keyOfArticulo, fmtMiles } from '@/utils/planProduccion/engine'
import { useFmtArticulo } from '@/hooks/useFmtArticulo'

const TOP_N = 5

type ViewMode = 'formato' | 'pedido'

interface Props {
  dark: boolean
  empresa: string
  semana: PlanSemana
  weekIdx: number
  /** Clave de material (itemid|color|estilo) → vista contextual filtrada. */
  materialKey: string | null
  engine: PlanEngine
  feed: PlanFeed
  onClose: () => void
}

export function WeekDrawer({ dark, empresa, semana, weekIdx, materialKey, engine, feed, onClose }: Props) {
  const { t } = useTranslation()
  const fmtArt = useFmtArticulo()
  const [view, setView] = useState<ViewMode>('formato')
  const [q, setQ] = useState('')
  const [soloRotura, setSoloRotura] = useState(false)
  const [orden, setOrden] = useState<'qty' | 'fecha'>('qty')
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [verMas, setVerMas] = useState<Record<string, boolean>>({})

  const { data, isLoading } = useQuery({
    queryKey: ['plan-entradas-detalle', empresa, semana.anio, semana.semana],
    queryFn: () => planProduccionApi.getEntradasDetalle({ empresa, anio: semana.anio, semana: semana.semana }),
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  })

  // Filtro contextual POR ARTÍCULO (itemid): dim_inventario es texto libre y
  // no permite casar la variante color/estilo de forma fiable.
  const materialItemid = materialKey ? materialKey.split('|')[0] : null

  // Material itemid → formatos cuyos artículos lo consumen (vía BOM)
  const formatosByItem = useMemo(() => {
    const m = new Map<string, Set<string>>()
    for (const a of feed.articulosPlanificables) {
      for (const sm of engine.subtreeMaterialsOf(keyOfArticulo(a))) {
        const itemid = sm.key.split('|')[0]
        let s = m.get(itemid)
        if (!s) {
          s = new Set()
          m.set(itemid, s)
        }
        s.add(a.formato)
      }
    }
    return m
  }, [feed, engine])

  // Materiales (itemid) con alguna variante bloqueante en negativo desde esta semana
  const shortageItems = useMemo(() => {
    const s = new Set<string>()
    for (const key of engine.materialByKey.keys()) {
      if (!engine.isBlocking(key)) continue
      for (let w = weekIdx; w < engine.numWeeks; w++) {
        if (engine.saldoFinAt(key, w) < -1e-9) {
          s.add(key.split('|')[0])
          break
        }
      }
    }
    return s
  }, [engine, weekIdx])

  /** Saldo proyectado a fin de semana del artículo = suma de sus variantes. */
  const saldoOfItem = (itemid: string): number | null => {
    let saldo = 0
    let found = false
    for (const key of engine.materialByKey.keys()) {
      if (key.split('|')[0] !== itemid) continue
      saldo += engine.saldoFinAt(key, weekIdx)
      found = true
    }
    return found ? saldo : null
  }

  // ── Líneas filtradas (contextual + rotura + búsqueda) ───────────────────────
  const lines = useMemo(() => {
    let rows = data?.data ?? []
    if (materialItemid) rows = rows.filter((r) => r.itemid === materialItemid)
    if (soloRotura) rows = rows.filter((r) => shortageItems.has(r.itemid))
    const qq = q.trim().toLowerCase()
    if (qq) {
      rows = rows.filter(
        (r) =>
          r.itemid.toLowerCase().includes(qq) ||
          r.descripcion.toLowerCase().includes(qq) ||
          r.purchid.toLowerCase().includes(qq) ||
          r.proveedor.toLowerCase().includes(qq),
      )
    }
    return rows
  }, [data, materialItemid, soloRotura, q, shortageItems])

  // ── Agrupación según la vista ────────────────────────────────────────────────
  const grupos = useMemo(() => {
    const map = new Map<string, { label: string; sub: string; rows: PlanEntradaDetalle[] }>()
    const push = (key: string, label: string, sub: string, r: PlanEntradaDetalle) => {
      let g = map.get(key)
      if (!g) {
        g = { label, sub, rows: [] }
        map.set(key, g)
      }
      g.rows.push(r)
    }
    for (const r of lines) {
      if (view === 'pedido') {
        push(r.purchid, r.purchid, r.proveedor, r)
      } else {
        const fs = formatosByItem.get(r.itemid)
        if (!fs || fs.size === 0) push('~otros', t('planFormato.wdSinFormato'), '', r)
        else for (const f of fs) push(f, f, '', r)
      }
    }
    const arr = [...map.entries()].map(([key, g]) => {
      const total = g.rows.reduce((s, r) => s + r.qty, 0)
      const fechas = g.rows.map((r) => r.fechaEntrada ?? '').filter(Boolean).sort()
      g.rows.sort((a, b) => (a.fechaEntrada ?? '').localeCompare(b.fechaEntrada ?? '') || b.qty - a.qty)
      return {
        key,
        label: g.label,
        sub: g.sub,
        rows: g.rows,
        total,
        minFecha: fechas[0] ?? '',
        shortage: g.rows.some((r) => shortageItems.has(r.itemid)),
      }
    })
    arr.sort((a, b) =>
      orden === 'qty' ? b.total - a.total : a.minFecha.localeCompare(b.minFecha) || b.total - a.total,
    )
    return arr
  }, [lines, view, formatosByItem, orden, shortageItems, t])

  const totalSemana = lines.reduce((s, r) => s + r.qty, 0)
  const searching = q.trim() !== ''

  const mat = materialKey ? engine.materialByKey.get(materialKey) : null
  const matDetail = materialKey ? engine.weekDetail(materialKey, weekIdx) : null

  // ── Estilos ──────────────────────────────────────────────────────────────────
  const bg = dark ? 'bg-slate-900' : 'bg-white'
  const bdr = dark ? 'border-white/10' : 'border-slate-200'
  const txtM = dark ? 'text-slate-100' : 'text-slate-800'
  const txtMut = dark ? 'text-slate-500' : 'text-slate-400'
  const rowBdr = dark ? 'border-white/5' : 'border-slate-100'
  const sectionTitle = clsx('text-[10px] font-bold uppercase tracking-wider px-4 pt-3 pb-2', txtMut)
  const statCard = clsx('rounded-lg border px-2.5 py-1.5', dark ? 'border-white/8 bg-white/[0.03]' : 'border-slate-100 bg-slate-50')
  const chipBase = 'text-[10px] font-bold px-2 py-1 rounded-lg border transition-colors cursor-pointer select-none'

  const stat = (label: string, value: number, tone?: string) => (
    <div className={statCard}>
      <div className={clsx('text-[8px] font-bold uppercase tracking-wider', txtMut)}>{label}</div>
      <div className={clsx('text-[12px] font-black tabular-nums', tone ?? txtM)}>{fmtMiles(value)}</div>
    </div>
  )

  const tabCls = (active: boolean) =>
    clsx(
      'flex-1 text-[10px] font-bold uppercase tracking-wide px-2 py-1.5 rounded-lg transition-colors cursor-pointer text-center',
      active
        ? dark ? 'bg-sky-500/20 text-sky-300' : 'bg-white text-sky-700 shadow-sm'
        : dark ? 'text-slate-500 hover:text-slate-300' : 'text-slate-400 hover:text-slate-600',
    )

  return (
    <>
      {/* Overlay */}
      <div className="plan-overlay-enter fixed inset-0 z-40 bg-black/30 backdrop-blur-[2px]" onClick={onClose} />
      {/* Drawer */}
      <aside className={clsx('plan-drawer-enter fixed right-0 top-0 z-50 h-full w-[420px] max-w-[94vw] flex flex-col border-l shadow-2xl', bg, bdr)}>
        {/* Cabecera */}
        <div className={clsx('flex items-center gap-3 px-4 py-3 border-b shrink-0', bdr)}>
          <div className={clsx('flex items-center justify-center w-10 h-10 rounded-xl text-[13px] font-black', dark ? 'bg-sky-500/15 text-sky-300' : 'bg-sky-100 text-sky-700')}>
            {semana.etiqueta}
          </div>
          <div className="flex-1 min-w-0">
            <div className={clsx('text-[13px] font-bold truncate', txtM)}>
              {materialKey ? mat?.descripcion ?? fmtArt(materialKey.split('|')[0]) : t('planFormato.wdTitulo')}
            </div>
            <div className={clsx('text-[10px]', txtMut)}>
              {semana.fechaInicio} → {semana.fechaFin}
              {materialKey && (
                <span className="ml-1">
                  · {[fmtArt(materialKey.split('|')[0]), ...materialKey.split('|').slice(1)].filter(Boolean).join(' · ')}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className={clsx('shrink-0 text-[12px] w-7 h-7 rounded-lg transition-colors cursor-pointer', dark ? 'text-slate-400 hover:bg-white/10' : 'text-slate-500 hover:bg-slate-100')}
          >
            ✕
          </button>
        </div>

        {/* Resumen del material (vista contextual) */}
        {materialKey && mat && matDetail && (
          <div className={clsx('shrink-0 border-b pb-3', bdr)}>
            <div className={sectionTitle}>{t('planFormato.wdInventario')}</div>
            <div className="grid grid-cols-3 gap-2 px-4">
              {stat(t('planFormato.wdStockTotal'), mat.stock.total)}
              {stat(t('planFormato.wdDispInicio'), engine.dispInicio(materialKey, weekIdx), dark ? 'text-sky-300' : 'text-sky-600')}
              {stat(
                t('planFormato.wdSaldoFin'),
                engine.saldoFinAt(materialKey, weekIdx),
                engine.saldoFinAt(materialKey, weekIdx) < 0 ? 'text-red-500' : dark ? 'text-emerald-400' : 'text-emerald-600',
              )}
              {stat(t('planFormato.wdComprometido'), matDetail.comprometidos, dark ? 'text-amber-300' : 'text-amber-600')}
              {stat(t('planFormato.wdSalidasOp'), matDetail.salidasOp)}
              {stat(t('planFormato.wdDemandaPlan'), engine.leafDemandAt(materialKey, weekIdx))}
            </div>
            <div className={clsx('mx-4 mt-2 px-2.5 py-1.5 rounded-lg text-[9px]', dark ? 'bg-white/[0.03] text-slate-500' : 'bg-slate-50 text-slate-400')}>
              {t('planFormato.stockAlmacenes', {
                gava: fmtMiles(mat.stock.gava), vid: fmtMiles(mat.stock.vid),
                rmt: fmtMiles(mat.stock.rmt), rmtDea: fmtMiles(mat.stock.rmtDea),
              })}
            </div>
          </div>
        )}

        {/* Toolbar: vistas + buscador + filtros */}
        <div className={clsx('shrink-0 px-3 pt-2.5 pb-2 border-b flex flex-col gap-2', bdr)}>
          {!materialItemid && (
            <div className={clsx('flex rounded-xl p-0.5', dark ? 'bg-white/5' : 'bg-slate-100')}>
              <button className={tabCls(view === 'formato')} onClick={() => setView('formato')}>
                {t('planFormato.wdVistaFormato')}
              </button>
              <button className={tabCls(view === 'pedido')} onClick={() => setView('pedido')}>
                {t('planFormato.wdVistaPedido')}
              </button>
            </div>
          )}
          <div className="flex items-center gap-1.5">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('planFormato.wdBuscar')}
              className={clsx(
                'flex-1 min-w-0 text-[11px] px-2.5 py-1.5 rounded-lg border outline-none transition-colors',
                dark
                  ? 'border-white/10 bg-slate-950 text-slate-200 focus:border-sky-500 placeholder:text-slate-600'
                  : 'border-slate-200 bg-white text-slate-700 focus:border-sky-400 placeholder:text-slate-300',
              )}
            />
            <button
              onClick={() => setSoloRotura((v) => !v)}
              title={t('planFormato.wdSoloRotura')}
              className={clsx(
                chipBase,
                soloRotura
                  ? 'bg-red-500 border-red-500 text-white'
                  : dark ? 'border-red-500/40 bg-red-500/10 text-red-300 hover:bg-red-500/20' : 'border-red-200 bg-red-50 text-red-600 hover:bg-red-100',
              )}
            >
              ⚠
            </button>
            <button
              onClick={() => setOrden((o) => (o === 'qty' ? 'fecha' : 'qty'))}
              title={t('planFormato.wdOrden')}
              className={clsx(chipBase, dark ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-white/10' : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50')}
            >
              {orden === 'qty' ? `↓ ${t('planFormato.wdOrdenQty')}` : `↑ ${t('planFormato.wdOrdenFecha')}`}
            </button>
          </div>
        </div>

        {/* Lista de grupos */}
        <div className="flex-1 overflow-y-auto scroll-thin">
          <div className={sectionTitle}>
            {t('planFormato.wdEntradas')}
            {lines.length > 0 && (
              <span className={clsx('ml-1.5 font-black', dark ? 'text-sky-300' : 'text-sky-600')}>
                {fmtMiles(totalSemana)}
              </span>
            )}
            <span className="ml-1.5 normal-case font-semibold">· {t('planFormato.wdLineas', { count: lines.length })}</span>
          </div>

          {isLoading && (
            <div className="px-4 flex flex-col gap-2">
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className={clsx('h-10 rounded-lg animate-pulse', dark ? 'bg-white/5' : 'bg-slate-100')} />
              ))}
            </div>
          )}

          {!isLoading && grupos.length === 0 && (
            <div className={clsx('px-4 py-6 text-center text-[11px]', txtMut)}>
              {soloRotura || searching ? t('planFormato.wdSinResultados') : t('planFormato.wdSinEntradas')}
            </div>
          )}

          {!isLoading && grupos.map((g) => {
            const isOpen = searching || !!expanded[g.key]
            const showAll = !!verMas[g.key]
            const visible = isOpen ? (showAll ? g.rows : g.rows.slice(0, TOP_N)) : []
            const hidden = g.rows.length - TOP_N
            return (
              <div key={g.key} className={clsx('mx-3 mb-2 rounded-xl border overflow-hidden', dark ? 'border-white/8' : 'border-slate-100 shadow-sm')}>
                {/* Cabecera-resumen del grupo */}
                <button
                  onClick={() => setExpanded((p) => ({ ...p, [g.key]: !p[g.key] }))}
                  className={clsx('w-full flex items-center gap-2 px-3 py-2 cursor-pointer transition-colors text-left', dark ? 'bg-white/[0.04] hover:bg-white/[0.07]' : 'bg-slate-50 hover:bg-slate-100')}
                >
                  <span className={clsx('text-[8px]', txtMut)}>{isOpen ? '▼' : '▶'}</span>
                  <div className="flex-1 min-w-0 leading-tight">
                    <div className={clsx('text-[11px] font-bold truncate flex items-center gap-1.5', txtM)}>
                      <span className="truncate">{g.label}</span>
                      {g.shortage && (
                        <span title={t('planFormato.wdSoloRotura')} className="shrink-0 text-[9px] text-red-500">⚠</span>
                      )}
                    </div>
                    <div className={clsx('text-[9px] truncate', txtMut)}>
                      {g.sub && <span>{g.sub} · </span>}
                      {t('planFormato.wdLineas', { count: g.rows.length })}
                      {g.minFecha && <span> · {g.minFecha.slice(8, 10)}/{g.minFecha.slice(5, 7)}</span>}
                    </div>
                  </div>
                  <span className={clsx('shrink-0 text-[11px] font-black tabular-nums', dark ? 'text-sky-300' : 'text-sky-600')}>
                    +{fmtMiles(g.total)}
                  </span>
                </button>

                {/* Líneas (top N + ver más) */}
                {visible.map((r, i) => {
                  const saldo = view === 'formato' && !materialItemid ? saldoOfItem(r.itemid) : null
                  return (
                    <div key={`${r.purchid}-${r.itemid}-${i}`} className={clsx('plan-row-enter flex items-center gap-2 px-3 py-1.5 border-t', rowBdr)}>
                      <div className="flex-1 min-w-0 leading-tight">
                        <div className={clsx('text-[10px] font-semibold truncate flex items-center gap-1', dark ? 'text-slate-200' : 'text-slate-700')}>
                          <span className="truncate">{r.descripcion}</span>
                          {shortageItems.has(r.itemid) && <span className="shrink-0 text-[8px] text-red-500">⚠</span>}
                        </div>
                        <div className={clsx('text-[9px] truncate', txtMut)} title={r.dimInventario || undefined}>
                          {view === 'pedido' ? fmtArt(r.itemid) : `${r.purchid} · ${r.proveedor}`}
                          {r.dimInventario && <span className="opacity-70"> · {r.dimInventario}</span>}
                        </div>
                      </div>
                      {saldo !== null && (
                        <span
                          className={clsx('shrink-0 text-[9px] tabular-nums', saldo < 0 ? 'text-red-500 font-bold' : txtMut)}
                          title={t('planFormato.wdSaldoFin')}
                        >
                          ⌀ {fmtMiles(saldo)}
                        </span>
                      )}
                      {r.fechaEntrada && (
                        <span className={clsx('shrink-0 text-[9px] tabular-nums', txtMut)}>
                          {r.fechaEntrada.slice(8, 10)}/{r.fechaEntrada.slice(5, 7)}
                        </span>
                      )}
                      <span className={clsx('shrink-0 w-14 text-right text-[10px] font-bold tabular-nums', txtM)}>
                        {fmtMiles(r.qty)}
                      </span>
                    </div>
                  )
                })}
                {isOpen && hidden > 0 && (
                  <button
                    onClick={() => setVerMas((p) => ({ ...p, [g.key]: !p[g.key] }))}
                    className={clsx('w-full text-center text-[10px] font-bold py-1.5 border-t transition-colors cursor-pointer', rowBdr, dark ? 'text-sky-400 hover:bg-white/[0.04]' : 'text-sky-600 hover:bg-slate-50')}
                  >
                    {showAll ? t('planFormato.wdVerMenos') : t('planFormato.wdVerMas', { count: hidden })}
                  </button>
                )}
              </div>
            )
          })}

          <div className="h-4" />
        </div>
      </aside>
    </>
  )
}
